import React, {useEffect, useMemo, useState} from 'react'
import ABAHeader from '../components/ABAComponents/ABAHeader'
import ABAAlert from '../components/ABAComponents/ABAAlert'
import RTIDAPI from '../../helpers/AxiosInstance'
import useUser from '../../libs/hooks/useUser'
import {Container, LoadingOverlay} from 'aba-ui'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableHead from '@mui/material/TableHead'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {ColorEnums} from '../../helpers/GeneralEnums'
import {openPdf} from '../../helpers/HelperMethods'
import useReport from '../../libs/hooks/useReport'

const headerCellStyle = {
    borderColor: ColorEnums.danube,
    fontSize: 14,
    color: ColorEnums.blueGray,
    fontFamily: 'lato',
}

const bodyCellStyle = {
    color: ColorEnums.cloudBurst,
    fontFamily: 'lato',
}

const DISPLAY_STATUS_REASON = 'Display on or after Display Date'

const parseDisplayDate = (value) => {
    if (!value) {
        return null
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value
    }

    if (typeof value === 'number') {
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date
    }

    if (typeof value !== 'string') {
        return null
    }

    const trimmed = value.trim()

    const dotNetMatch = trimmed.match(/^\/Date\((-?\d+)([+-]\d{4})?\)\/$/)
    if (dotNetMatch?.[1]) {
        const millis = Number(dotNetMatch[1])
        if (Number.isFinite(millis)) {
            const date = new Date(millis)
            return Number.isNaN(date.getTime()) ? null : date
        }
    }

    const iso = new Date(trimmed)
    if (!Number.isNaN(iso.getTime())) {
        return iso
    }

    const parts = trimmed.split('/')
    if (parts.length === 3) {
        const [mm, dd, yyyy] = parts
        const month = Number(mm)
        const day = Number(dd)
        const year = Number(yyyy)
        if (month && day && year) {
            const date = new Date(year, month - 1, day)
            return Number.isNaN(date.getTime()) ? null : date
        }
    }

    return null
}

const matchesDisplayStatusReason = (statusReason) => {
    const normalized = String(statusReason ?? '')
        .trim()
        .toLowerCase()
    const target = DISPLAY_STATUS_REASON.toLowerCase()
    return normalized === target || normalized.includes(target)
}

const getStatusReason = (assignment) => {
    return (
        assignment?.StatusReason ??
        assignment?.StatusReasonName ??
        assignment?.statusReason ??
        assignment?.statusReasonName ??
        ''
    )
}

const getFileLocation = (assignment) => {
    return assignment?.FileLocation ?? assignment?.FileUrl ?? assignment?.Url ?? assignment?.url ?? ''
}

const getEventLabel = (assignment) => {
    return (
        assignment?.EventLabel ??
        assignment?.EventName ??
        assignment?.Event ??
        assignment?.eventLabel ??
        assignment?.eventName ??
        assignment?.event ??
        ''
    )
}

const getEventYear = (assignment) => {
    const explicit = assignment?.EventYear ?? assignment?.eventYear
    const yearNumber = Number(explicit)
    if (Number.isInteger(yearNumber) && yearNumber > 1900) {
        return yearNumber
    }

    const displayDate = parseDisplayDate(assignment?.DisplayDate ?? assignment?.displayDate)
    if (displayDate) {
        return displayDate.getFullYear()
    }

    const label = getEventLabel(assignment)
    const match = typeof label === 'string' ? label.match(/\b(19|20)\d{2}\b/) : null
    if (match) {
        return Number(match[0])
    }

    return null
}

const isAssignmentVisible = (assignment, now = new Date()) => {
    const statusReason = getStatusReason(assignment)
    if (String(statusReason ?? '').trim() && !matchesDisplayStatusReason(statusReason)) {
        return false
    }

    const displayDate = parseDisplayDate(assignment?.DisplayDate ?? assignment?.displayDate)
    if (!displayDate) {
        return false
    }

    return displayDate.getTime() <= now.getTime()
}

export default function UnsatisfactoryLetters() {
    const {userData} = useUser()
    const [loading, setLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [assignments, setAssignments] = useState([])
    const {getPostedDocument} = useReport()

    const canView =
        userData?.UserRole === 'ProgramDirector' ||
        userData?.UserRole === 'DepartmentChair' ||
        userData?.UserRole === 'SuperUser'

    useEffect(() => {
        let isMounted = true
        const controller = new AbortController()

        const load = async () => {
            if (!canView) {
                return
            }
            setErrorMessage('')
            setLoading(true)
            try {
                const {data} = await RTIDAPI.get('report/unsatisfactoryLetters', {
                    signal: controller.signal,
                    timeout: 30000,
                })
                if (isMounted) {
                    setAssignments(Array.isArray(data) ? data : [])
                }
            } catch (error) {
                if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                    return
                }
                console.error(error)
                if (isMounted) {
                    setErrorMessage('An error occurred while fetching Unsatisfactory Letters')
                    setAssignments([])
                }
            } finally {
                if (isMounted) {
                    setLoading(false)
                }
            }
        }

        load()

        return () => {
            isMounted = false
            controller.abort()
        }
    }, [canView])

    const visibleAssignments = useMemo(() => {
        const visible = (assignments || []).filter((a) => isAssignmentVisible(a))
        return visible
    }, [assignments])

    const openAssignment = async (assignment) => {
        setErrorMessage('')

        const location = getFileLocation(assignment)
        if (location) {
            window.open(location, '_blank', 'noopener,noreferrer')
            return
        }

        const documentId = assignment?.DocumentID ?? assignment?.DocumentId ?? assignment?.documentId
        if (!documentId) {
            return
        }

        try {
            setLoading(true)
            const document = await getPostedDocument(documentId)
            openPdf(document)
            setLoading(false)
        } catch (error) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return
            }
            console.error(error)
            setErrorMessage('An error occurred while loading your letter')
        } finally {
            setLoading(false)
        }
    }

    const hasAny = visibleAssignments.length > 0

    return (
        <>
            <ABAHeader text="Unsatisfactory Letters" />
            {errorMessage && (
                <ABAAlert
                    text={errorMessage}
                    style={{
                        width: 520,
                        margin: 'auto',
                    }}
                />
            )}

            {!canView ? (
                <ABAAlert
                    text="You do not have access to view Unsatisfactory Letters."
                    style={{width: 520, margin: 'auto'}}
                />
            ) : (
                <Container title="Unsatisfactory Letters" headerJustify="center">
                    <Table sx={{margin: 'auto'}} style={{tableLayout: 'fixed', maxWidth: 900}}>
                        <colgroup>
                            <col />
                            <col style={{width: '120px'}} />
                            <col style={{width: '40px'}} />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell style={headerCellStyle}>Document</TableCell>
                                <TableCell style={headerCellStyle}>Year</TableCell>
                                <TableCell style={headerCellStyle}></TableCell>
                            </TableRow>
                        </TableHead>
                        {hasAny ? (
                            <TableBody>
                                {visibleAssignments.map((assignment, idx) => {
                                    const displayName =
                                        assignment?.DisplayName ??
                                        assignment?.displayName ??
                                        assignment?.Name ??
                                        assignment?.name ??
                                        'Letter'

                                    const year =
                                        getEventYear(assignment) ??
                                        parseDisplayDate(
                                            assignment?.DisplayDate ?? assignment?.displayDate,
                                        )?.getFullYear() ??
                                        ''

                                    const key = `${assignment?.DocumentID || assignment?.documentId || idx}-${displayName}`

                                    return (
                                        <TableRow
                                            key={key}
                                            onClick={() => openAssignment(assignment)}
                                            hover
                                            style={{cursor: 'pointer'}}
                                        >
                                            <TableCell style={bodyCellStyle}>{displayName}</TableCell>
                                            <TableCell style={bodyCellStyle}>{year}</TableCell>
                                            <TableCell style={{color: ColorEnums.danube}}>
                                                <FontAwesomeIcon icon={'angle-right'} />
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        ) : (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        colSpan={3}
                                        style={{textAlign: 'center', ...bodyCellStyle}}
                                    >
                                        No Records to Display
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                    </Table>
                    {loading && <LoadingOverlay fullscreen={false} />}
                </Container>
            )}
        </>
    )
}
